import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Undo2 } from 'lucide-react'
import { getPORemaining, getReceipt } from '../../api/purchase'
import ErrorBanner from '../../components/ErrorBanner'
import Spinner from '../../components/Spinner'
import { BackLink, DocHeader } from '../../components/PageParts'
import { fmtDay } from '../../utils/format'
import { docNo } from '../../utils/docNumbers'
import { money, num } from '../sales/salesShared'
import { MaterialTag, useMaterialIds } from './itemFor'

export default function ReceiptDetail() {
  const materialIds = useMaterialIds()
  const { id } = useParams()
  const [hdr, setHdr] = useState(null)
  const [lines, setLines] = useState([])
  const [returns, setReturns] = useState([])
  const [returnNos, setReturnNos] = useState({}) // return_id -> tenant number
  const [returnedQty, setReturnedQty] = useState(0)
  const [returnedValue, setReturnedValue] = useState(0)
  const [stillToCome, setStillToCome] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const data = await getReceipt(id)
        if (cancelled) return
        setHdr(data.header || null)
        setLines(data.lines || [])
        setReturns(data.returns_by_product || [])
        setReturnNos(Object.fromEntries((data.returns || []).map((r) => [r.return_id, r.return_no])))
        setReturnedQty(num(data.returned_qty_total))
        setReturnedValue(num(data.returned_total))
        if (data?.header?.po_id) {
          const rem = await getPORemaining(data.header.po_id, { net: false }).catch(() => [])
          if (!cancelled) setStillToCome((rem || []).filter((r) => num(r.remaining) > 0))
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Could not load this receipt.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [id])

  return (
    <div className="ci-form-page">
      <DocHeader
        back={<BackLink to="/purchase/receipts" label="Receiving" />}
        title={docNo('receipt', hdr?.receipt_no) || 'Goods receipt'}
        meta={hdr && (
          <>
            {hdr.vendor_id ? <Link to={`/purchase/ap/vendor/${hdr.vendor_id}`}>{hdr.vendor_name || `Vendor #${hdr.vendor_id}`}</Link> : hdr.vendor_name}
            {hdr.receipt_date && ` · received ${fmtDay(hdr.receipt_date)}`}
            {hdr.po_id && <> · <Link to={`/purchase/po-detail/${hdr.po_id}`}>{docNo('po', hdr.po_no)}</Link></>}
          </>
        )}
        actions={hdr?.po_id && (
          <Link className="ci-btn ci-btn-ghost" to={`/purchase/vendor-returns/new?po_id=${hdr.po_id}&receipt_id=${hdr.receipt_id || id}`}><Undo2 size={14} />Return goods to vendor</Link>
        )}
      />

      {error && <ErrorBanner message={error} />}

      {loading ? <Spinner label="Loading receipt…" /> : hdr && (
        <>
          {hdr.notes && <p className="doc-hint">Note: {hdr.notes}</p>}

          <section className="doc-card">
            <div className="doc-card-head">
              <h2>What arrived</h2>
              <span>{lines.length} {lines.length === 1 ? 'item' : 'items'}</span>
            </div>
            <div className="doc-table-scroll">
              <table className="ci-line-table">
                <thead><tr><th>Item</th><th className="num">Received</th></tr></thead>
                <tbody>
                  {lines.map((l, i) => (
                    <tr key={`${l.product_id}-${i}`}>
                      <td>{l.name || l.sku}<MaterialTag kind={materialIds.has(Number(l.product_id)) ? 'material' : ''} /><span className="cell-sub">{l.sku}</span></td>
                      <td className="num">{num(l.quantity).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {hdr.po_id && (
            <section className="doc-card">
              <div className="doc-card-head">
                <h2>Still to come on {docNo('po', hdr.po_no)}</h2>
                <span>{stillToCome.length ? 'Not delivered yet' : 'Everything has arrived'}</span>
              </div>
              {stillToCome.length ? (
                <div className="doc-table-scroll">
                  <table className="ci-line-table">
                    <thead><tr><th>Item</th><th className="num">Still to come</th></tr></thead>
                    <tbody>
                      {stillToCome.map((r) => (
                        <tr key={r.product_id}>
                          <td>{r.name || r.sku}<span className="cell-sub">{r.sku}</span></td>
                          <td className="num">{num(r.remaining).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="doc-empty">Nothing else is due on this order.</p>
              )}
            </section>
          )}

          {returns.length > 0 && (
            <section className="doc-card">
              <div className="doc-card-head">
                <h2>Sent back to the vendor</h2>
                <span>{returnedQty.toLocaleString()} {returnedQty === 1 ? 'unit' : 'units'} · {money(returnedValue)}</span>
              </div>
              <div className="doc-table-scroll">
                <table className="ci-line-table">
                  <thead><tr><th>Item</th><th className="num">Returned</th><th className="num">Value</th><th>Returns</th></tr></thead>
                  <tbody>
                    {returns.map((r, i) => (
                      <tr key={`${r.product_id}-${i}`}>
                        <td>{r.name || r.sku}<span className="cell-sub">{r.sku}</span></td>
                        <td className="num">{num(r.total_qty).toLocaleString()}</td>
                        <td className="num">{money(r.total_amount)}</td>
                        <td>{(r.return_ids || []).map((rid, j) => <span key={rid}>{j > 0 && ', '}<Link className="ci-je-link" to={`/purchase/vendor-returns/${rid}`}>{docNo('return', returnNos[rid]) || 'Return'}</Link></span>)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  )
}
